import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules, getFormattedFiatCurrencyNumber } from '@utils';
import {
  useLocation,
  useFiatCurrency,
  useNotifications,
  useSubstitutedHolderName,
  useNavigate,
} from '@utils/hooks';
import { useUpdateBillingAddressFromKYC } from '@utils/hooks';
import { cardCVVMask, cardDateMask, cardNumberMask, pinMask } from '@utils/mask';
import { LocationStateType } from '@utils/types';

import { cardsActions, globalActions, operationBlockActions, useStore } from '@modules';
import { ZHModalTypeEnum } from '@modules/global/types';
import { OperationNextEnum } from '@modules/types';

import Button from '@components/Button';
import Input from '@components/Input';
import InputMask from '@components/InputMask';
import { DataKeysEnum } from '@containers/OperationBlock/types';
import MainLayout from '@layouts/MainLayout';
import { checkShowBillingAddress } from '@selectors';

import style from './AddCard.scss';

const cn = bemCssModules(style, 'AddCard');

export type TAddCard = {
  title?: string;
  backRoute?: (typeof CONST.nav)[keyof typeof CONST.nav];
  verificationRoute?: (typeof CONST.nav)[keyof typeof CONST.nav];
};

export const AddCard: CustomFC<TAddCard> = props => {
  const { className, title, backRoute, verificationRoute } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stateStore, dispatch] = useStore();
  const { cards } = stateStore;
  const { bindStatus, card3DS, card } = cards;
  const { number, cvv, expDate, holderName } = card;

  const substitutedHolderName = useSubstitutedHolderName();
  const { bindCardFailedNotification, bindCardErrorNotification } = useNotifications();

  const [flutterwavePin, setFlutterwavePin] = useState('');
  const [showPin, setShowPin] = useState(false);

  const location = useLocation<LocationStateType['addCard']>();
  const { state } = location;
  const amount = state?.amount;
  const currency = state?.currency;
  const showFailedNotification = state?.showFailedNotification;
  const fiatCurrencyDefault = useFiatCurrency();
  const fiatCurrency = currency || fiatCurrencyDefault;

  const { updateBillingAddressFromKYC, isLoading: isLoadingUpdateBillingAddressFromKYC } =
    useUpdateBillingAddressFromKYC({
      redirectTo: CONST.nav.operationWithNewCard,
      notificationData: { message: t('common.new_billing_address_notification') },
    });

  useEffect(() => {
    dispatch(cardsActions.clearAllErrors());
    if (showFailedNotification) bindCardFailedNotification();
    return () => {
      dispatch(cardsActions.clearAllErrors());
      dispatch(cardsActions.resetCardData());
    };
  }, []);

  useEffect(() => {
    if (card3DS.redirect) navigate(verificationRoute || CONST.nav.profileCardsVerify);
  }, [card3DS.redirect]);

  useEffect(() => {
    setFlutterwavePin('');
  }, [bindStatus]);

  useEffect(() => {
    if (cards.errors.message && Array.isArray(cards.errors.data)) {
      bindCardErrorNotification(cards.errors.name || cards.errors.title, cards.errors.message);
      dispatch(cardsActions.clearAllErrors());
    }
  }, [cards.errors]);

  const getError = (name: string) => cards.errors.data?.[name]?.[0];

  const resetError = (name: string) => {
    if (!getError(name)) return;

    dispatch(cardsActions.clearError(name));
  };

  const handleChangeCardNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    resetError('number');
    const { value } = event.target;
    dispatch(cardsActions.setCard({ name: 'number', value }));
  };

  const handleChangeHolderName = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!substitutedHolderName) {
      const { value } = event.target;
      dispatch(cardsActions.setCard({ name: 'holderName', value }));
    }
  };

  const handleChangeExpDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    resetError('expiration_year');
    resetError('expiration_month');
    const { value } = event.target;
    dispatch(cardsActions.setCard({ name: 'expDate', value }));
  };

  const handleChangeCVV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    dispatch(cardsActions.setCard({ name: 'cvv', value }));
  };

  const handleChangeFlutterWavePin = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFlutterwavePin(event.target.value);
  };

  const getRawCardNumber = (n: string) => n.replace(/ /g, '');

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Check billing address if it is missing, you must first go to the screen and fill it in.
    const showBillingAddress = checkShowBillingAddress(stateStore);
    if (showBillingAddress) {
      const isAddressUpdated = await updateBillingAddressFromKYC();

      if (!isAddressUpdated) return;
    }

    const [cardMM, cardYY] = expDate.split('/');
    const bindCardParams = {
      number: getRawCardNumber(number),
      cvv,
      holder_name: holderName,
      expiration_year: `20${cardYY}`,
      expiration_month: cardMM,
      pin: flutterwavePin || undefined,
    };

    dispatch(cardsActions.bind({ ...bindCardParams }))
      .unwrap()
      .then(({ data }) => {
        dispatch(cardsActions.clearAllErrors());
        if (data.status === CONST.card3DS.statuses.succeeded) {
          navigate(backRoute || CONST.nav.profile);
        }
        if (data.next === OperationNextEnum.pin) setShowPin(true);
        if (data.next === OperationNextEnum.otp) {
          navigate<'verifyOtp'>(CONST.nav.verifyOtp, {
            state: {
              redirectTo: backRoute || CONST.nav.profile,
              id: data.id,
            },
          });
        }
        if (data.next === OperationNextEnum.zhAgree) {
          dispatch(globalActions.setZHModalType(ZHModalTypeEnum.bind));
        } else {
          dispatch(cardsActions.resetCardData());
        }
        dispatch(operationBlockActions.setLeft({ group: DataKeysEnum.cards, value: data.id }));
        dispatch(cardsActions.clearAllErrors());
      })
      .catch(err => err);
  };

  const flutterwavePinPlaceholder = CONST.mask.flutterWave.pin.replace(/[0-9]/g, '•');

  const isSubmitDisabled =
    !number.length ||
    expDate.length < 5 ||
    cvv.length < cardCVVMask.mask.length ||
    !holderName ||
    (showPin && flutterwavePin.length < CONST.mask.flutterWave.pin.length);

  const isLoading = cards.loaders.bind;

  const buttonText = amount
    ? getFormattedFiatCurrencyNumber({ amount: Number(amount), currency: fiatCurrency })
    : t('card.add');

  const onBack = () => navigate(backRoute || CONST.nav.profile);

  return (
    <MainLayout className={cn(null, [className])} backButton={onBack}>
      <div className={cn('content')}>
        <h1 className={cn('title')}>{title || t('card.add_new_card')}</h1>
        <form className={cn('form')} onSubmit={onSubmit}>
          <div className={cn('row')}>
            <InputMask
              className={cn('input')}
              placeholder="0000 0000 0000 0000"
              label={t('card.number')}
              onChange={handleChangeCardNumber}
              autoComplete="cc-number"
              maskOptions={cardNumberMask}
              disabled={isLoading}
              error={getError('number')}
              value={number}
            />
          </div>
          <div className={cn('row')}>
            <InputMask
              className={cn('input')}
              placeholder={t('card.expdate')}
              label={t('card.exp_label')}
              onChange={handleChangeExpDate}
              autoComplete="cc-exp"
              maskOptions={cardDateMask}
              disabled={isLoading}
              error={getError('expiration_year') || getError('expiration_month')}
              value={expDate}
            />
            <InputMask
              className={cn('cvv')}
              maskOptions={cardCVVMask}
              type="password"
              label="CVC/CVV"
              onChange={handleChangeCVV}
              autoComplete="cc-csc"
              disabled={isLoading}
              value={cvv}
              showMask={false}
            />
            {showPin && (
              <InputMask
                label="Pin number"
                autoFocus
                className={cn('pin')}
                maskOptions={pinMask}
                type="password"
                autoComplete="off"
                placeholder={flutterwavePinPlaceholder}
                value={flutterwavePin}
                onChange={handleChangeFlutterWavePin}
                disabled={isLoading}
                showMask={false}
              />
            )}
          </div>
          <div className={cn('row')}>
            <Input
              className={cn('input')}
              placeholder={t('card.holder_name')}
              label={t('card.holder_name')}
              onChange={handleChangeHolderName}
              autoComplete="cc-name"
              disabled={isLoading || !!substitutedHolderName}
              value={holderName}
              error={getError('holder_name')}
            />
          </div>

          <div className={cn('button-wrapper')}>
            <Button
              className={cn('button')}
              type="submit"
              disabled={isSubmitDisabled}
              isLoading={isLoading || isLoadingUpdateBillingAddressFromKYC}
            >
              {buttonText}
            </Button>

            <div className={cn('additional', ['small'])}>{t('card.additional_info')}</div>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default AddCard;
